// src/components/alertesCritiques/PanneauAlertesCritiques.jsx
// Le panneau CENTRAL des alertes critiques : les anomalies qui demandent une
// intervention (montants mal saisis, classes sans matières, demandes de
// correction à valider), groupées par détecteur, avec l'action proposée sur
// chaque ligne. Les lignes, les groupes et le constat sont des pièces
// communes avec le panneau latéral (PiecesAlertes.jsx, disposition 'large').
//
// Le serveur compose tout : le constat, la correction présumée, l'impact,
// l'action et le lien. Le panneau ne calcule rien, il met en page.
//   - « Corriger »           : l'écran de correction ; le panneau se ferme et
//                              revient au bout de 2 minutes (au plus
//                              l'intervalle) avec les lignes restantes.
//                              PONT (GO de Daniel du 16/10/2026) : sur un
//                              écran d'au moins 1 280 px (pontActif), la suite
//                              s'affiche dans le panneau latéral, ouvert avec
//                              les mêmes données ; le panneau central se ferme
//                              sans rappel court (onPont fait tout) ;
//   - badge « autre année »  : une correction à faire dans une session ouverte
//                              sur l'année de la ligne (l'écran de correction
//                              est lié à l'année de la session) ;
//   - « Ouvrir la demande »  : la demande spéciale visée, dans sa fenêtre du
//                              Centre de Support (?tab=special&demande=<id>),
//                              même retour que « Corriger », pont compris ;
//   - « Constater »          : la valeur restera telle quelle (validateurs,
//                              motif obligatoire) ; la ligne ne revient que si
//                              la valeur change. Jamais sur une information.
//                              Chaque ligne garde son motif : passer d'une
//                              ligne à l'autre ne perd rien de ce qui est tapé ;
//   - badge « en attente »   : une correction attend sa validation ;
//   - « Voir la liste »      : une information qui porte son propre lien (les
//                              élèves ou le personnel sans photo) mène à la
//                              liste filtrée ; le panneau se ferme comme au
//                              pied, d'un intervalle entier, sans rappel court :
//                              rien n'y est à corriger dans l'immédiat ;
//   - rien                   : simple information (une information sans lien
//                              propre n'a jamais de bouton, même quand son
//                              détecteur a un lien).
//
// Gravité « campagne » (À COMPLÉTER) : un manque à combler peu à peu, pas une
// faute. Ton gris neutre, jamais ambre ni rouge (règle de
// parametrage/PhotosEleves.jsx : le manque de photo est neutre). Ses lignes
// sont des informations : elles ne comptent jamais « à traiter », n'ouvrent ni
// ne font sonner le panneau, et s'affichent quand il s'ouvre pour autre chose.
//
// À l'ouverture, le focus va sur le cadre du panneau, pas sur le premier
// bouton : une frappe destinée à la page (Espace, Entrée) ne doit pas
// déclencher « Corriger » et quitter un formulaire en cours.
//
// Ni le clic à l'extérieur ni Échap ne ferment le panneau (décisions de Daniel
// du 13/10/2026) : seuls « Me rappeler dans N min », « Corriger », « Ouvrir
// la demande » et « Voir la liste » le referment. Échap replie seulement le
// motif ouvert (abandon, comme « Annuler »). Pendant l'envoi d'un constat,
// rien ne ferme le panneau.
//
// Pas de « ne plus afficher » (décision de Daniel du 13/10/2026) : le seul
// bouton du pied, « Me rappeler dans N min », repousse le rappel d'un
// intervalle. Le panneau disparaît de lui-même quand il n'y a plus rien à
// traiter dans cette session. Une infobulle à côté du titre explique le
// rythme du panneau et le bouton « Alertes » de la barre, qui le suspend
// (demande de Daniel du 16/10/2026).
//
// Les gestes sont journalisés pour la page de suivi par l'ordonnanceur
// (useAlertesCritiques) ; le panneau lui transmet seulement la clé de la ligne
// partie en correction, ou dont la liste a été ouverte.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import InfoBulle from '../ui/InfoBulle';
import PorteurNotification from '../ui/PorteurNotification';
import { useNotification } from '../../hooks/useNotification';
import { anneeDeSession, compterATraiter, delaiRappelCourt } from './useAlertesCritiques';
import {
  BOUTON_SECONDAIRE,
  GroupeDetecteur,
  LigneAlerte,
  VerificationsEnEchec,
  pluriel,
  useConstats
} from './PiecesAlertes';

// Le bouton du pied tient sur une ligne : la note voisine se replie, pas lui.
const BOUTON_PIED = 'h-9 min-w-[200px] shrink-0 whitespace-nowrap justify-center gap-2 px-4';

function resumeATraiter(nombre) {
  if (nombre <= 0) return 'Plus rien à traiter';
  return `${nombre} ${pluriel(nombre, 'point', 'points')} à traiter`;
}

/** Le clic (ou le focus) à l'extérieur ne ferme jamais le panneau. */
const garderOuvert = (evenement) => evenement.preventDefault();

/**
 * @param {boolean} ouvert
 * @param {object} donnees - la réponse de GET /api/alertes-critiques
 * @param {number} intervalleMinutes - délai avant le prochain rappel
 * @param {(options?: { rappelCourt?: boolean, cle?: string }) => void} onFermer -
 *   repousse le rappel d'un intervalle ; `rappelCourt` : retour rapide après
 *   « Corriger », `cle` : la ligne partie en correction, ou dont la liste a
 *   été ouverte (journal du suivi)
 * @param {(detecteurId: string, cle: string) => void} onLigneConstatee
 * @param {() => Promise<boolean>} onActualiser - relit les alertes sans toucher au rappel
 * @param {boolean} pontActif - écran d'au moins 1 280 px : « Corriger » passe par le pont
 * @param {(cible: { lien: string, cle: string, detecteurId: string }) => boolean} onPont -
 *   rend true si le pont est pris (navigation, fermeture sans rappel court,
 *   panneau latéral ouvert, journal : tout est fait)
 */
export default function PanneauAlertesCritiques({
  ouvert, donnees, intervalleMinutes, onFermer, onLigneConstatee, onActualiser, pontActif = false, onPont = null
}) {
  const navigate = useNavigate();
  const { notification, notifier, masquer, signalerEchec } = useNotification();
  const constats = useConstats({ onLigneConstatee, onActualiser, notifier, signalerEchec });
  const { enConstat, motifs, envoi, envoiEnCours, reinitialiser } = constats;
  const [deplies, setDeplies] = useState({});
  const contenuRef = useRef(null);

  // Chaque ouverture repart d'un panneau propre.
  useEffect(() => {
    if (!ouvert) return;
    reinitialiser();
    setDeplies({});
  }, [ouvert, reinitialiser]);

  const anneeSession = useMemo(() => (ouvert ? anneeDeSession() : null), [ouvert]);
  const detecteurs = useMemo(() => donnees?.detecteurs || [], [donnees]);
  // Ce qui se traite dans cette session : sans les corrections d'une autre année.
  const aTraiter = compterATraiter(donnees, anneeSession);
  const minutes = intervalleMinutes || 30;
  const minutesCourt = delaiRappelCourt(minutes);

  const fermer = () => {
    if (envoiEnCours.current) return;
    onFermer();
  };

  // L'agent part corriger une ligne. Grand écran : le pont ouvre la suite dans
  // le panneau latéral. Sinon le panneau revient vite avec les autres.
  const corriger = (lien, cle, detecteurId) => {
    if (envoiEnCours.current) return;
    if (onPont && onPont({ lien, cle, detecteurId })) return;
    navigate(lien);
    onFermer({ rappelCourt: true, cle });
  };

  // L'agent ouvre la liste d'une information (élèves sans photo…) : rien n'y
  // est à corriger dans l'immédiat, le panneau revient à l'intervalle ordinaire.
  // Le geste est journalisé comme une correction, avec la clé de la ligne.
  const voirListe = (lien, cle) => {
    if (envoiEnCours.current) return;
    navigate(lien);
    onFermer({ cle });
  };

  // Échap ne ferme jamais le panneau (décision de Daniel du 13/10/2026) : il
  // replie seulement le motif ouvert, comme « Annuler ».
  const surEchap = (evenement) => {
    evenement.preventDefault();
    if (enConstat) constats.annulerConstat();
  };

  // Le focus va sur le cadre (tabIndex -1 posé par Radix), où Espace et Entrée
  // ne font rien : Radix le donnerait sinon au premier « Corriger ».
  const focaliserLeCadre = (evenement) => {
    evenement.preventDefault();
    contenuRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      {/* Clic extérieur et Échap toujours retenus : seuls le pied, « Corriger »,
          « Ouvrir la demande » et « Voir la liste » ferment. */}
      <Dialog open={ouvert}>
        <DialogContent
          ref={contenuRef}
          className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0 focus:outline-none"
          onOpenAutoFocus={focaliserLeCadre}
          onPointerDownOutside={garderOuvert}
          onInteractOutside={garderOuvert}
          onEscapeKeyDown={surEchap}
        >
          <DialogHeader className="flex-row items-start gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-lg font-bold text-gray-900">Alertes critiques - à traiter</DialogTitle>
                <InfoBulle titre="Comment ça marche" libelle="Comment fonctionne ce panneau" alignement="start">
                  Ce panneau revient {minutes > 1 ? `toutes les ${minutes} minutes` : 'chaque minute'} tant
                  qu'il reste des alertes. Pour les traiter à votre rythme, ouvrez « Alertes » en haut
                  à droite : tant que ce panneau latéral est ouvert, celui-ci ne revient pas.
                </InfoBulle>
              </div>
              <DialogDescription className="mt-1 text-sm text-gray-500">
                Ces points demandent votre intervention. Ce panneau se rouvrira
                dans {minutes} {pluriel(minutes, 'minute', 'minutes')} tant qu'ils ne sont pas réglés.
              </DialogDescription>
            </div>
            <span
              className={`inline-flex h-7 flex-shrink-0 items-center rounded-lg px-2.5 text-xs font-semibold ${
                aTraiter > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {resumeATraiter(aTraiter)}
            </span>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {detecteurs.map((detecteur) => (
              <GroupeDetecteur
                key={detecteur.id}
                detecteur={detecteur}
                deplie={Boolean(deplies[detecteur.id])}
                onDeplier={() => setDeplies((precedent) => ({ ...precedent, [detecteur.id]: true }))}
              >
                {(ligne) => {
                  const cleConstat = `${detecteur.id}|${ligne.cle}`;
                  return (
                    <LigneAlerte
                      key={ligne.cle}
                      ligne={ligne}
                      lienDetecteur={detecteur.lien}
                      anneeSession={anneeSession}
                      enConstat={enConstat === cleConstat}
                      motif={motifs[cleConstat] || ''}
                      envoi={envoi}
                      onMotif={(texte) => constats.changerMotif(cleConstat, texte)}
                      onCorriger={(lien) => corriger(lien, ligne.cle, detecteur.id)}
                      onVoirListe={(lien) => voirListe(lien, ligne.cle)}
                      onConstater={() => constats.ouvrirConstat(cleConstat)}
                      onConfirmer={() => constats.confirmerConstat(detecteur.id, ligne)}
                      onAnnuler={constats.annulerConstat}
                    />
                  );
                }}
              </GroupeDetecteur>
            ))}

            <VerificationsEnEchec erreurs={donnees?.erreurs} suite="au prochain rappel" />
          </div>

          <DialogFooter className="items-center gap-2 border-t bg-gray-50 px-5 py-4 sm:justify-between sm:gap-4 sm:space-x-0">
            <p className="min-w-0 text-xs text-gray-500">
              {pontActif
                ? 'Après « Corriger », la suite s\'affiche dans le panneau à droite.'
                : `Après « Corriger », le panneau revient au bout de ${minutesCourt} min, sans les lignes corrigées.`}
            </p>
            <Button type="button" className={`${BOUTON_PIED} ${BOUTON_SECONDAIRE}`} onClick={fermer} disabled={envoi}>
              <Clock className="h-4 w-4 shrink-0" />
              Me rappeler dans {minutes} min
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PorteurNotification notification={notification} onFermer={masquer} />
    </>
  );
}
